/**
 * Utility functions for Google Meet room code generation and URL validation.
 * Google Meet strictly requires a 10-letter room code in the format: xxx-yyyy-zzz (3 letters - 4 letters - 3 letters).
 */

export function generateValidMeetCode(): string {
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  const pick = (len: number) => Array.from({ length: len }, () => letters[Math.floor(Math.random() * letters.length)]).join('');
  return `${pick(3)}-${pick(4)}-${pick(3)}`;
}

export function isValidMeetCode(code: string): boolean {
  if (!code) return false;
  const clean = code.trim().toLowerCase();
  return /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/.test(clean);
}

export function extractMeetCode(urlOrCode: string): string {
  if (!urlOrCode) return generateValidMeetCode();
  const clean = urlOrCode.trim().toLowerCase();
  
  // If it's already a code matching 3-4-3
  if (/^[a-z]{3}-[a-z]{4}-[a-z]{3}$/.test(clean)) {
    return clean;
  }
  
  // Try extracting from URL
  const match = clean.match(/meet\.google\.com\/([a-z0-9-]+)/i);
  if (match && match[1]) {
    const candidate = match[1].toLowerCase();
    if (/^[a-z]{3}-[a-z]{4}-[a-z]{3}$/.test(candidate)) {
      return candidate;
    }
  }

  // If candidate had letters only, attempt formatting
  const rawLetters = clean.replace(/[^a-z]/g, '');
  if (rawLetters.length >= 10) {
    return `${rawLetters.slice(0, 3)}-${rawLetters.slice(3, 7)}-${rawLetters.slice(7, 10)}`;
  }

  return generateValidMeetCode();
}

export function ensureValidMeetLink(urlOrCode?: string): string {
  const code = extractMeetCode(urlOrCode || '');
  return `https://meet.google.com/${code}`;
}

export function generateUniqueMeetLink(): string {
  return ensureValidMeetLink(generateValidMeetCode());
}
